import React, { useState, createContext, useContext } from "react";
import { Toaster } from "react-hot-toast";
import { useLocation } from "react-router-dom";
import "../styles/layout.css";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import FrontendLayout from "../layouts/FrontendLayout";
import AppRoutes from "./routes";
import { useAuth } from "../context/AuthContext";

// Create a context for sidebar state
interface SidebarContextType {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  // Club admin sidebar shares its mobile open/close state across the layout
  // and the topbar so the hamburger lives in the page header, not in <main>.
  clubAdminMenuOpen: boolean;
  setClubAdminMenuOpen: (open: boolean) => void;
  toggleClubAdminMenu: () => void;
  /**
   * True while ClubAdminLayout is on screen, wherever it mounted.
   *
   * It brings its own header; the outer shell has to stand down or the page
   * ends up with two. Keyed on the layout actually being there rather than on
   * the path, because a club admin reaches it from routes that have nothing to
   * do with /dashboard/admin-club — their own profile, for one.
   */
  clubPanelMounted: boolean;
  setClubPanelMounted: (mounted: boolean) => void;
}

const SidebarContext = createContext<SidebarContextType>({
  sidebarOpen: true,
  setSidebarOpen: () => {},
  toggleSidebar: () => {},
  clubAdminMenuOpen: false,
  setClubAdminMenuOpen: () => {},
  toggleClubAdminMenu: () => {},
  clubPanelMounted: false,
  setClubPanelMounted: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

export default function App() {
  const location = useLocation();
  const { token, activeRole, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [clubAdminMenuOpen, setClubAdminMenuOpen] = useState(false);
  const [clubPanelMounted, setClubPanelMounted] = useState(false);

  // Pages that should not show the header/sidebar layout
  const publicPages = ["/login", "/register", "/invite", "/verify-email"];
  const isPublicPage = publicPages.some(page => location.pathname.startsWith(page));

  // Mobile app pages (/app/*) have their own layout (MobileAppLayout)
  const isMobileAppPage = location.pathname.startsWith("/app") || location.pathname.startsWith("/quiz/");

  // Show full layout only if authenticated and not on a public page. While
  // auth is still refreshing we deliberately show NO chrome — otherwise, on
  // login / cold start, `activeRole` is briefly null and the code below would
  // flash the platform-admin sidebar wrapper for ~1s before the real role
  // resolves. During loading the routes render a full-screen branded loader.
  const showLayout = token && !isPublicPage && !isMobileAppPage && !loading;

  // Only the platform admin gets the sidebar wrapper. Everyone else (and any
  // not-yet-resolved role) uses the sidebar-less FrontendLayout, so a null
  // role never lands on the admin chrome.
  const useFrontendLayout = activeRole !== "platform_admin";

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const toggleClubAdminMenu = () => setClubAdminMenuOpen((prev) => !prev);

  return (
    <SidebarContext.Provider
      value={{
        sidebarOpen,
        setSidebarOpen,
        toggleSidebar,
        clubAdminMenuOpen,
        setClubAdminMenuOpen,
        toggleClubAdminMenu,
        clubPanelMounted,
        setClubPanelMounted,
      }}
    >
      <Toaster
        position="top-right"
        /*
          Clear of the status bar. In the native app a top-right toast landed
          on the signal, wifi and battery icons — the notch zone is inside the
          web view because the viewport is `viewport-fit=cover` — so the
          confirmation you most wanted to read was the one you could not. The
          inset is 0 on the web, where the 1rem gap is all that is needed.
        */
        containerStyle={{
          top: "calc(env(safe-area-inset-top, 0px) + 1rem)",
          right: "1rem",
        }}
        toastOptions={{
          duration: 4000,
          style: {
            background: "#1e293b",
            color: "#e2e8f0",
            border: "1px solid #334155",
            borderRadius: "0.75rem",
            fontSize: "0.875rem",
            fontWeight: "500",
          },
          success: {
            iconTheme: {
              primary: "#10b981",
              secondary: "#fff",
            },
            style: {
              border: "1px solid #059669",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
            style: {
              border: "1px solid #dc2626",
            },
          },
        }}
      />

      {isMobileAppPage ? (
        // Mobile app pages have their own layout (MobileAppLayout)
        <AppRoutes />
      ) : showLayout ? (
        useFrontendLayout ? (
          // Modern layout without sidebar for Coach/Athlete
          <FrontendLayout>
            <AppRoutes />
          </FrontendLayout>
        ) : (
          // Admin layout with sidebar for Platform Admin. The Topbar now lives
          // inside <main> (like the club layout) so the sidebar starts at the
          // very top and the header spans only the content column.
          <div className="wrapper">
            <div className={`content ${!sidebarOpen ? 'sidebar-collapsed' : ''}`}>
              <div className={`sidebar-wrapper ${!sidebarOpen ? 'collapsed' : ''}`}>
                <Sidebar />
              </div>
              <main className="main">
                <Topbar />
                <AppRoutes />
              </main>
            </div>
          </div>
        )
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <AppRoutes />
        </div>
      )}
    </SidebarContext.Provider>
  );
}
